import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../../../app/hooks';
import BottomDrawer from '../../../../common/_custom/BottomDrawer/BottomDrawer';
import { Button } from '../../../../common/_custom';
import { ComponentCellRenderer, DataTable, DataTableColumnType } from '../../../../common/_custom';
import { CellRendererProps } from '../../../../common/_custom/DataTable/DataTable.types';
import { renderFnProps } from '../../../../common/_custom/DataTable/components/CellRenderers/ComponentCellRenderer/ComponentCellRenderer';
import { DESIGNATION } from '../../../../utils/constants/commonConstants';
import { COLORS } from '../../../../utils/constants/colors';
import { PillCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/PillCellRenderer/PillCellRenderer';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import { isEmpty, truncateByCharacters } from '../../../../utils/utils';
import { editIconPurple } from '../../../../assets/hostedassets';
import { mandateTermsMapping, mandateSharingColumns, defaultAggregatedDealTerms } from '../../DebtProductsConstants';
import styles from './MandateSharingDrawer.module.scss';
import { getAggregatedMandateData } from '../../../../store/debtProducts/apiHelper_debtProducts';
import { AggregatedDealTerms } from '../../debtProductTypes';
import LenderUserPillCell, { Option } from '../LenderUserPillCell/LenderUserPillCell';
import { renderMandateShimmer } from '../../DebtProductsUtil';

interface MandateSharingDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onShareWithLender?: () => void;
    onEndUseOfCapitalChange?: (value: string) => void;
    initialEndUseOfCapital?: string;
    dealId: string;
    onUpdateSelectedUsers?: (dealReceiverId: string, users: Array<{ name: string; userId: string }>) => void;
    selectedUsers?: Record<
        string,
        {
            userIds: Array<{ name: string; userId: string }>;
            type: string;
        }
    >;
}

function MandateSharingDrawer({
    isOpen,
    onClose,
    onShareWithLender,
    onEndUseOfCapitalChange,
    initialEndUseOfCapital = '',
    dealId,
    onUpdateSelectedUsers,
    selectedUsers = {},
}: MandateSharingDrawerProps) {
    const updateLenderUsers = (rowId: string, selectedUserIds: string[], options: Option[]) => {
        const selectedUsers = options
            .filter((opt) => selectedUserIds.includes(opt.value) && opt.type !== DESIGNATION.ADMIN)
            .map((opt) => ({
                name: opt.label,
                userId: opt.value,
            }));
        onUpdateSelectedUsers?.(rowId, selectedUsers);
    };

    const componentRenderFnRegistry = ({ row, column }: renderFnProps) => {
        switch (column?.field) {
            case 'lenderUsers': {
                if (!row?.['lenderUsers']) return <div style={{ color: COLORS.GREY }}>-</div>;
                // Get the selected users for this row from parent state (full objects with name and userId)
                const parentSelectedUsers = selectedUsers[row?.dealReceiverId]?.userIds || [];
                return (
                    <LenderUserPillCell
                        users={row?.['lenderUsers'] || []}
                        rowId={row?.dealReceiverId}
                        isShared={row?.isShared ?? 0}
                        onUsersUpdate={updateLenderUsers}
                        parentSelectedUsers={parentSelectedUsers}
                    />
                );
            }
            default:
                return null;
        }
    };

    const CustomPillCellRenderer: React.FC<CellRendererProps> = ({ value }) => {
        if (isEmpty(value)) {
            return <span style={{ color: COLORS.GREY }}>-</span>;
        }

        if (!Array.isArray(value)) {
            return <PillCellRenderer label={value ? 'Shared' : 'Not Shared'} isActive={value} />;
        }
        const [firstItem, ...rest] = value;
        const remainingCount = rest.length;

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <PillCellRenderer label={truncateByCharacters(firstItem, 20)} isActive={false} />

                {remainingCount > 0 && (
                    <Tooltip
                        content={
                            <div style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
                                {value.map((item, idx) => (
                                    <div key={idx}>• {item}</div>
                                ))}
                            </div>
                        }
                        direction="bottom"
                        usePortal={true}
                        hideDelay={400}
                        style={{
                            position: 'absolute',
                            bottom: '100%',
                            top: 'auto',
                            transform: 'translateX(-50%)',
                            marginBottom: '0.5rem',
                            marginLeft: '0',
                            zIndex: 99999,
                            maxWidth: '400px',
                            minWidth: '300px',
                        }}
                        contentCss={{
                            borderRadius: '8px',
                            position: 'relative',
                            zIndex: 99999,
                            boxShadow: '0px 0px 10px 2px #0000001A',
                            maxHeight: '200px',
                            overflowY: 'auto',
                        }}
                    >
                        <span style={{ color: '#6021B3', fontSize: '12px', cursor: 'pointer' }}>+{remainingCount} more</span>
                    </Tooltip>
                )}
            </div>
        );
    };

    const [rows, setRows] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [aggregatedMandateTerms, setAggregatedMandateTerms] = useState<AggregatedDealTerms>(defaultAggregatedDealTerms);

    const getMandateTermConfig = (key: string) => {
        return mandateTermsMapping.find((config) => config.key === key);
    };

    useEffect(() => {
        if (isEmpty(dealId)) return;
        setIsLoading(true);
        getAggregatedMandateData(dealId, {
            onSuccess: (data) => {
                setAggregatedMandateTerms(data?.aggregatedDealTerms || defaultAggregatedDealTerms);
                setRows(data?.lenders || []);
                setIsLoading(false);
            },
            onError: () => {
                setAggregatedMandateTerms(defaultAggregatedDealTerms);
                setRows([]);
                setIsLoading(false);
            },
        });
    }, [dealId]);

    const [isEditingEndUse, setIsEditingEndUse] = useState(false);
    const [editEndUseValue, setEditEndUseValue] = useState<string>('');

    // Sync initialEndUseOfCapital from props when drawer opens
    useEffect(() => {
        if (isOpen && initialEndUseOfCapital) {
            setAggregatedMandateTerms((prev) => ({
                ...prev,
                EndUseofCapital: initialEndUseOfCapital,
            }));
        }
    }, [isOpen, initialEndUseOfCapital]);

    const pinnedColumns = useMemo(
        () => [
            { field: 'lenderName', position: 'left' as const },
            { field: 'lenderUsers', position: 'right' as const },
            { field: 'isShared', position: 'right' as const },
        ],
        [],
    );

    const processedRows = useMemo(() => {
        return rows.map((row) => ({
            ...row,
            rowStyle: row?.isShared === 1 ? { backgroundColor: '#F7F7F7' } : undefined,
        }));
    }, [rows]);

    const emptyScreenComponent = (
        <div style={{ padding: '40px', textAlign: 'center' }}>
            <p>No mandates to display</p>
        </div>
    );

    if (!isOpen) return null;

    const renderReadOnlyItem = (key, value) => {
        switch (key) {
            case 'minimumRoi': {
                const minRoi = aggregatedMandateTerms.minimumRoi;
                const maxRoi = aggregatedMandateTerms.maximumRoi;
                if (minRoi !== undefined && maxRoi !== undefined) {
                    const roiConfig = getMandateTermConfig('roi');
                    return (
                        <div className={styles.AmountItem}>
                            <span className={styles.Label}>{roiConfig?.label || 'ROI'}:</span>
                            <span className={styles.Value}>{minRoi === maxRoi ? `${minRoi}%` : `${minRoi}-${maxRoi}%`}</span>
                        </div>
                    );
                }
                return null;
            }
            case 'minimumTenure': {
                const minTenure = aggregatedMandateTerms.minimumTenure;
                const maxTenure = aggregatedMandateTerms.maximumTenure;
                if (minTenure !== undefined && maxTenure !== undefined) {
                    const tenureConfig = getMandateTermConfig('tenure');
                    return (
                        <div className={styles.AmountItem}>
                            <span className={styles.Label}>{tenureConfig?.label || 'Tenure'}:</span>
                            <span className={styles.Value}>
                                {minTenure === maxTenure ? `${minTenure} m` : `${minTenure}-${maxTenure} m`}
                            </span>
                        </div>
                    );
                }
                return null;
            }
            case 'maximumRoi':
            case 'maximumTenure':
                return null;
            case 'maximumDealAmount': {
                const config = getMandateTermConfig(key);
                if (!config) return null;
                return (
                    <div className={styles.AmountItem}>
                        <span className={styles.Label}>{config.label}:</span>
                        <span className={styles.Value}>
                            {value} {config.unit || ''}
                        </span>
                    </div>
                );
            }
            case 'products':
            case 'securityOffered': {
                const config = getMandateTermConfig(key);
                if (!config) return null;
                return (
                    <div className={styles.ProductsItem}>
                        <span className={styles.Label}>{config.label}:</span>
                        <div className={styles.ValueContainer}>
                            <Tooltip
                                content={
                                    <div style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
                                        {value.map((item, index) => (
                                            <div key={index}>• {item}</div>
                                        ))}
                                    </div>
                                }
                                direction="bottom"
                                fitContent={true}
                                usePortal={true}
                                hideDelay={400}
                                style={{
                                    position: 'absolute',
                                    bottom: '100%',
                                    top: 'auto',
                                    transform: 'translateX(-50%)',
                                    marginBottom: '0.5rem',
                                    marginLeft: '0',
                                    zIndex: 99999,
                                    maxWidth: '400px',
                                    minWidth: '300px',
                                }}
                                contentCss={{
                                    borderRadius: '8px',
                                    position: 'relative',
                                    zIndex: 99999,
                                    boxShadow: '0px 0px 10px 2px #0000001A',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                }}
                            >
                                <span className={styles.Value}>{truncateByCharacters(value[0] || '', 20)}</span>
                            </Tooltip>
                            {value.length > 1 && (
                                <Tooltip
                                    content={
                                        <div style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
                                            {value.map((item, index) => (
                                                <div key={index}>• {item}</div>
                                            ))}
                                        </div>
                                    }
                                    direction="bottom"
                                    fitContent={true}
                                    usePortal={true}
                                    hideDelay={400}
                                    style={{
                                        position: 'absolute',
                                        bottom: '100%',
                                        top: 'auto',
                                        transform: 'translateX(-50%)',
                                        marginBottom: '0.5rem',
                                        marginLeft: '0',
                                        zIndex: 99999,
                                        maxWidth: '400px',
                                        minWidth: '300px',
                                    }}
                                    contentCss={{
                                        borderRadius: '8px',
                                        position: 'relative',
                                        zIndex: 99999,
                                        boxShadow: '0px 0px 10px 2px #0000001A',
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                    }}
                                >
                                    <span className={styles.MoreCount}>+ {value.length - 1} more</span>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                );
            }
            default:
                return null;
        }
    };
    const renderEditableItem = (key, value) => {
        const config = getMandateTermConfig(key);
        if (!config) return null;

        switch (key) {
            case 'EndUseofCapital':
                const hasValue = !isEmpty(value);

                if (isEditingEndUse) {
                    return (
                        <div className={styles.EndUseItem}>
                            <span className={styles.Label}>{config.label}:</span>
                            <div className={styles.InputContainer}>
                                <input
                                    type="text"
                                    value={editEndUseValue}
                                    onChange={handleEndUseInputChange}
                                    onBlur={handleEndUseInputBlur}
                                    onKeyDown={handleEndUseInputKeyDown}
                                    onClick={(e) => e.stopPropagation()}
                                    className={styles.EndUseInput}
                                    autoFocus
                                />
                            </div>
                        </div>
                    );
                }

                return (
                    <div className={styles.EndUseItem}>
                        <span className={styles.Label}>{config.label}:</span>
                        <div className={styles.EndUseValue}>
                            {hasValue ? (
                                <>
                                    <Tooltip content={value} direction="bottom" fitContent={true}>
                                        <span className={styles.ValueText}>{truncateByCharacters(value, 40)}</span>
                                    </Tooltip>
                                    <button className={styles.EditButton} onClick={handleEndUseClick} type="button">
                                        <span className={styles.EditText}>Edit</span>
                                        <img src={editIconPurple} alt="Edit" className={styles.EditIcon} />
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className={styles.EmptyValue}>-</div>
                                    <button className={styles.EditButton} onClick={handleEndUseClick} type="button">
                                        <span className={styles.AddText}>Add</span>
                                        <img src={editIconPurple} alt="Add" className={styles.EditIcon} />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    const handleEndUseClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsEditingEndUse(true);
        setEditEndUseValue(aggregatedMandateTerms.EndUseofCapital || '');
    };

    const handleEndUseInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEditEndUseValue(e.target.value);
    };

    const handleEndUseInputBlur = () => {
        setIsEditingEndUse(false);
        if (editEndUseValue !== aggregatedMandateTerms.EndUseofCapital) {
            setAggregatedMandateTerms({
                ...aggregatedMandateTerms,
                EndUseofCapital: editEndUseValue,
            });
            // Call parent callback to update dealTerms
            onEndUseOfCapitalChange?.(editEndUseValue);
        }
    };

    const handleEndUseInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleEndUseInputBlur();
        } else if (e.key === 'Escape') {
            setIsEditingEndUse(false);
            setEditEndUseValue(aggregatedMandateTerms.EndUseofCapital || '');
        }
    };

    return (
        <BottomDrawer heading="Review Mandate" allowClose={true} onClose={onClose} showHeading={true} height="90%">
            <div className={styles.MandateSummaryContainer}>
                <div className={styles.MandateSummary}>Mandate Summary</div>
                <div className={styles.MandateSummaryContent}>
                    {isLoading ? (
                        <div className={styles.ReadOnlyContent}>{renderMandateShimmer(5, styles)}</div>
                    ) : (
                        <div className={styles.ReadOnlyContent}>
                            {Object.entries(aggregatedMandateTerms).map(([key, value]) => renderReadOnlyItem(key, value))}
                        </div>
                    )}
                    {isLoading ? (
                        <div className={styles.EditableContent}>{renderMandateShimmer(1, styles)}</div>
                    ) : (
                        <div className={styles.EditableContent}>
                            {Object.entries(aggregatedMandateTerms).map(([key, value]) => renderEditableItem(key, value))}
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.LenderSummaryContainer}>
                <div className={styles.LenderSummary}>Lender Summary</div>
                <div className={styles.DataTableContainer}>
                    <DataTable
                        columns={mandateSharingColumns}
                        rows={processedRows}
                        loading={isLoading}
                        emptyState={emptyScreenComponent}
                        totalRowCount={rows.length}
                        pinnedColumns={pinnedColumns}
                        customCellRenderers={{
                            [DataTableColumnType.COMPONENT]: (props) => (
                                <ComponentCellRenderer
                                    {...props}
                                    dynamicProps={{
                                        componentRenderFnRegistry,
                                    }}
                                />
                            ),
                            [DataTableColumnType.PILL]: CustomPillCellRenderer,
                        }}
                    />
                </div>
            </div>
            <BottomDrawer.Footer>
                <Button
                    text="Share with Lender(s)"
                    onClick={() => onShareWithLender?.()}
                    variant="purple"
                    size="normal"
                    style={{ marginBottom: '20px' }}
                />
            </BottomDrawer.Footer>
        </BottomDrawer>
    );
}

export default MandateSharingDrawer;
